import { createApp } from 'vue';
import JsonCSV from 'vue-json-csv';
import * as Sentry from '@sentry/vue';
import shouldInitSentry from '@/logic/monitoring/shouldInitSentry';
import Oruga, { useOruga } from '@oruga-ui/oruga-next';
import VueTelInput from 'vue-tel-input';
import { bulmaConfig } from '@oruga-ui/theme-bulma';
import { createGtm } from '@gtm-support/vue-gtm';
import { Amplify } from 'aws-amplify';
import AmplifyVue from '@aws-amplify/ui-vue';
import '@aws-amplify/ui-vue/styles.css';
import axios from 'axios';
import VueAxios from 'vue-axios';
import CmsHelpers from '@/logic/cms/helpers';
import ProfileHelpers from '@/logic/profile/helpers';
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './i18n';
import './vee-validate';
import '@/scss/app.scss';
import '@/assets/fonts/lwm/lwm.css';
import awsExports from './aws-exports';
import './unregisterServiceWorker';
import 'vue-tel-input/vue-tel-input.css';
import { setToast } from './mixins/toastService';
import { patchFetch } from './composables/useWafCaptcha';

Amplify.configure(awsExports);

// Patch window.fetch before any Cognito calls so the WAF token from the
// cookie is injected on page refresh (when the CAPTCHA widget never fires).
patchFetch();

const app = createApp(App);

// Never for a local dev server — see shouldInitSentry for why this is PROD and
// not MODE. `vite build --mode e2e` stays excluded by the DSN check, since
// .env.e2e sets VUE_APP_SENTRY_DSN=''.
if (shouldInitSentry(import.meta.env)) {
  Sentry.init({
    app,
    dsn: import.meta.env.VUE_APP_SENTRY_DSN,
    release: import.meta.env.VUE_APP_RELEASE,
    // Drop noise from scripts injected by in-app browsers (e.g. the Google app's
    // WKWebView on iOS), which surface as recursion errors with no app frames.
    ignoreErrors: ['Maximum call stack size exceeded'],
    denyUrls: [
      /^chrome-extension:\/\//,
      /^safari-extension:\/\//,
      /^moz-extension:\/\//,
    ],
    beforeSend(event, hint) {
      // Drop Axios errors that never received a server response: aborted
      // requests (AbortController on filter/route change) and client-side
      // network drops. These carry no HTTP status and are not actionable.
      // Real server errors keep their `response` (4xx/5xx) and pass through.
      const error = hint?.originalException as
        { isAxiosError?: boolean; response?: unknown } | undefined;
      if (error?.isAxiosError && !error.response) {
        return null;
      }

      // Discard exceptions whose stack trace has no in-app frame at all — these
      // originate entirely from injected/third-party scripts, not our bundle.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (frames.length > 0 && !frames.some((frame) => frame.in_app)) {
        return null;
      }
      return event;
    },
  });
}

app.use(router);

app.use(
  createGtm({
    id: 'GTM-PVG8NJ74',
    vueRouter: router,
  }),
);

app.use(AmplifyVue);

axios.defaults.baseURL = import.meta.env.VUE_APP_BASE_PATH;
app.use(VueAxios, axios);

app.config.globalProperties.$helpers = CmsHelpers;
app.config.globalProperties.$profile = ProfileHelpers;

app.use(VueTelInput);

const customConfig = {
  customIconPacks: {
    lwm: {
      sizes: {
        default: 'is-small',
        'is-small': 'is-size-5',
        'is-medium': 'is-size-3',
        'is-large': 'is-size-1',
      },
      iconPrefix: 'lwm-',
    },
  },
  iconPack: 'lwm',
};

const config = { ...bulmaConfig, ...customConfig };

const { oruga } = useOruga();
app.provide('$oruga', oruga);

app.component('downloadCsv', JsonCSV);

app.use(Oruga, config);
app.use(store);
app.use(i18n);

setToast(app.config.globalProperties.$oruga);
app.mount('#app');
